import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { APP_NAME } from '../config/index.js';
import { OPERATOR_HOME } from '../config/routeAccess.js';
import './OperatorShell.scss';

const AREA_NAME = 'Operator console';
// `match` decides both the highlighted tab and the browser tab title; the customer detail page belongs to Customers.
const NAV = [
  { to: OPERATOR_HOME, label: 'Customers', icon: 'fa-building', testid: 'operator-nav-customers', match: (p) => p === OPERATOR_HOME || p.startsWith(`${OPERATOR_HOME}/customers`) },
  { to: `${OPERATOR_HOME}/plans`, label: 'Plans & prices', icon: 'fa-tags', testid: 'operator-nav-plans', match: (p) => p.startsWith(`${OPERATOR_HOME}/plans`) },
  { to: `${OPERATOR_HOME}/activity`, label: 'Activity', icon: 'fa-history', testid: 'operator-nav-activity', match: (p) => p.startsWith(`${OPERATOR_HOME}/activity`) },
];

/**
 * Shell of the Xamun Operator console (/admin). Deliberately not AppShell: no tenant branding, no tenant menus,
 * and a different top bar so it is obvious this is the vendor's area and not a customer's workspace.
 */
export default function OperatorShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const path = pathname.replace(/\/+$/, '') || '/';
  const current = NAV.find((item) => item.match(path));

  // Per-page browser tab title. The customer detail page replaces it with the customer's name once loaded.
  useEffect(() => {
    document.title = current ? `${current.label} · ${AREA_NAME}` : AREA_NAME;
  }, [current]);

  useEffect(() => {
    setAccountOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const onDown = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setAccountOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/sign-in');
  };

  return (
    <div className="operator-shell" data-testid="operator-shell">
      <header className="operator-topbar">
        <div className="operator-topbar-inner">
          <Link className="operator-brand" to={OPERATOR_HOME} data-testid="operator-brand">
            <i className="fas fa-user-shield operator-brand-icon" aria-hidden="true"></i>
            <span className="operator-brand-text">
              {APP_NAME} <span className="operator-brand-sep">·</span> <span className="operator-brand-area">{AREA_NAME}</span>
            </span>
          </Link>

          <nav className="operator-nav" aria-label={AREA_NAME} data-testid="operator-nav">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={'operator-nav-link' + (current === item ? ' active' : '')}
                aria-current={current === item ? 'page' : undefined}
                data-testid={item.testid}
              >
                <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="operator-account" ref={accountRef}>
            <button
              type="button"
              className="operator-account-btn"
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              aria-label="Account menu"
              data-testid="operator-account-btn"
              onClick={() => setAccountOpen((o) => !o)}
            >
              <i className="fas fa-user-circle" aria-hidden="true"></i>
              <span className="operator-account-email">{user?.email}</span>
              <i className="fas fa-chevron-down operator-account-caret" aria-hidden="true"></i>
            </button>
            {accountOpen && (
              <div className="operator-account-menu shadow" role="menu" data-testid="operator-account-menu">
                <div className="operator-account-who">
                  <span className="d-block small text-muted">Signed in as</span>
                  <span className="fw-semibold" data-testid="operator-account-email">
                    {user?.email}
                  </span>
                </div>
                <button type="button" className="operator-account-item text-danger" role="menuitem" data-testid="operator-sign-out-btn" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt me-2" aria-hidden="true"></i> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="operator-main">
        <Outlet />
      </main>
    </div>
  );
}
